import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Divider, Form, Input, Segmented, Space, Typography } from "antd";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { MeDto, MfaRequiredDto } from "@audit-plan/shared";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../api/client";
import { setLang } from "../i18n";
import { palette } from "../theme";
import { useIsMobile } from "../components/useIsMobile";
import { fetchSsoConfig, startSso } from "../auth/sso";

export function BrandBlock() {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: palette.primary, color: "#fff", display: "grid", placeItems: "center", fontWeight: 800 }}>AP</div>
      <div style={{ lineHeight: 1.2 }}>
        <b style={{ color: palette.heading }}>{t("appName")}</b>
        <div style={{ fontSize: 12, color: palette.muted }}>{t("appSub")}</div>
      </div>
    </div>
  );
}

export function LangSwitch() {
  const { i18n } = useTranslation();
  return (
    <Segmented
      size="small"
      value={i18n.language === "en" ? "en" : "it"}
      options={[
        { label: "IT", value: "it" },
        { label: "EN", value: "en" },
      ]}
      onChange={(v) => setLang(v as "it" | "en")}
    />
  );
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { me, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const mobile = useIsMobile();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const sso = useQuery({ queryKey: ["sso-config"], queryFn: fetchSsoConfig, staleTime: 5 * 60_000, retry: false });
  if (me) return <Navigate to="/" replace />;

  const done = async () => {
    await refresh();
    const from = (loc.state as { from?: string } | null)?.from;
    nav(from && from !== "/login" ? from : "/", { replace: true });
  };

  const submit = async (v: { email: string; password: string }) => {
    setBusy(true);
    setErr(null);
    try {
      // sensitive-ok: invio del form, pagina pubblica
      const r = await api.post<MeDto | MfaRequiredDto>("/auth/login", { email: v.email.trim(), password: v.password });
      if ("mfaRequired" in r) setMfaToken(r.mfaToken);
      else await done();
    } catch (e) {
      setErr(e instanceof ApiError ? (e.status === 401 ? t("a_loginBad") : e.message) : t("a_loginFail"));
    } finally {
      setBusy(false);
    }
  };

  const goSso = async () => {
    if (!sso.data?.enabled) return;
    setBusy(true);
    setErr(null);
    try {
      const from = (loc.state as { from?: string } | null)?.from;
      await startSso(sso.data, from);
    } catch {
      setErr(t("c_ssoFailed"));
      setBusy(false);
    }
  };

  const submitCode = async (v: { code: string }) => {
    setBusy(true);
    setErr(null);
    try {
      await api.post("/auth/login/totp", { mfaToken, code: v.code.trim() });
      await done();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401 && /scadut|expired/i.test(e.message)) {
        setMfaToken(null);
        setErr(t("c_mfaExpired"));
      } else setErr(e instanceof ApiError ? e.message : t("a_loginFail"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: palette.bg, padding: mobile ? 12 : 16 }}>
      <Card style={{ width: "100%", maxWidth: 440 }} styles={{ body: { padding: mobile ? 20 : 28 } }}>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <BrandBlock />
            <LangSwitch />
          </div>
          <div>
            <Typography.Title level={3} style={{ margin: 0, color: palette.heading }}>
              {mfaToken ? t("c_mfaTitle") : t("loginTitle")}
            </Typography.Title>
            <Typography.Text type="secondary">{mfaToken ? t("c_mfaSub") : t("a_loginSub")}</Typography.Text>
          </div>
          {err && <Alert type="error" showIcon message={err} />}
          {mfaToken ? (
            <Form layout="vertical" onFinish={submitCode} requiredMark={false} disabled={busy}>
              <Form.Item name="code" label={t("c_mfaCode")} rules={[{ required: true, message: t("required") }]}>
                <Input autoComplete="one-time-code" autoFocus size="large" inputMode="text" maxLength={32} style={{ fontSize: 20, letterSpacing: 2 }} />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={busy} style={{ height: 48 }}>
                {t("c_mfaVerify")}
              </Button>
              <Button type="link" block onClick={() => { setMfaToken(null); setErr(null); }} style={{ marginTop: 8 }}>
                {t("c_mfaBack")}
              </Button>
            </Form>
          ) : (
          <>
          {sso.data?.enabled && (
            <div>
              <Button type="primary" block size="large" onClick={goSso} loading={busy} style={{ height: 48 }}>
                {t("c_ssoLogin")}
              </Button>
              <Divider plain style={{ margin: "20px 0 0", fontSize: 13, color: palette.muted }}>
                {t("c_ssoOr")}
              </Divider>
            </div>
          )}
          <Form layout="vertical" onFinish={submit} requiredMark={false} disabled={busy}>
            <Form.Item name="email" label={t("email")} rules={[{ required: true, type: "email", message: t("a_emailInvalid") }]}>
              <Input autoComplete="username" autoFocus={!mobile} size="large" inputMode="email" style={{ fontSize: 16 }} />
            </Form.Item>
            <Form.Item name="password" label={t("a_password")} rules={[{ required: true, message: t("required") }]}>
              <Input.Password autoComplete="current-password" size="large" style={{ fontSize: 16 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={busy} style={{ height: 48 }}>
              {t("a_login")}
            </Button>
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Link to="/forgot">{t("c_forgotLink")}</Link>
            </div>
          </Form>
          </>
          )}
        </Space>
      </Card>
    </div>
  );
}
